import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { IS_PREVIEW } from "../config";
import ui from "../components/ui.module.css";

const MESSAGES = {
  empty: "Enter your email and password.",
  invalid: "That email and password didn't work. Check them and try again.",
  offline: "You're offline. Connect to the internet to sign in.",
  unknown: "We couldn't sign you in right now. Please try again.",
};

const SignIn = () => {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const destination = location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (status === "signed-in") return <Navigate to={destination} replace />;

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError(MESSAGES.empty);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(MESSAGES[err.reason] || MESSAGES.unknown);
      setBusy(false);
    }
  };

  return (
    <main className={`${ui.page} ${ui.narrow}`}>
      <img
        src="/thuto-login.svg"
        alt="Thuto Time"
        style={{ maxWidth: "100%", maxHeight: "40vh", alignSelf: "center" }}
      />
      <div className={ui.stack}>
        <h1 className={ui.title}>Sign in</h1>
        <p className={ui.meta}>Sign in to see the lessons you're teaching.</p>
      </div>

      {IS_PREVIEW && (
        <p className={`${ui.notice} ${ui.warning}`}>
          Preview mode: any email and password will open sample lessons.
        </p>
      )}

      <form className={ui.stack} onSubmit={submit} noValidate>
        <div className={ui.field}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className={ui.input}
            type="email"
            inputMode="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={error ? "sign-in-error" : undefined}
          />
        </div>
        <div className={ui.field}>
          <label htmlFor="password">Password</label>
          <div className={ui.inputWrap}>
            <input
              id="password"
              className={ui.input}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={error ? "sign-in-error" : undefined}
            />
            <button
              type="button"
              className={ui.textButton}
              onClick={() => setShowPassword((shown) => !shown)}
              aria-pressed={showPassword}
              aria-controls="password"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {error && (
          <p id="sign-in-error" className={ui.error} role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className={`${ui.button} ${ui.primary} ${ui.full}`}
          disabled={busy}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
};

export default SignIn;
